/**
 * Listados operativos read-only por unidad de negocio.
 *
 * Estos componentes reutilizan los endpoints existentes filtrados por
 * `?unidad_negocio=slug` para mostrar Compras, Producción, Proveedores y
 * Hojas Operativas dentro del panel de cada unidad, sin duplicar módulos.
 *
 * El objetivo es que el operador de cada unidad tenga visibilidad de sus
 * datos sin salir del panel. La edición sigue haciéndose en los módulos
 * globales (para no fragmentar los flujos de contrato → producción → compra).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Link } from "react-router-dom";
import { ShoppingCart, Factory, Truck, Layers, ExternalLink } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

/* ═══════════════════════════════════════════════════════════════════
   COMPRAS TAB · listado de compras filtradas por unidad
   ═══════════════════════════════════════════════════════════════════ */
export function ComprasTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/compras?unidad_negocio=${unidadSlug}`);
      setItems(r.data || []);
    } catch { toast.error("Error al cargar compras"); }
    finally { setLoading(false); }
  }, [unidadSlug]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const total = items.reduce((s, c) => s + (c.total || c.costo_total || 0), 0);

  return (
    <div className="space-y-4" data-testid={`compras-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Compras</h2>
          <p className="text-stone-500 text-sm">Órdenes de compra de la unidad. Total: <strong>{fmt(total)}</strong>.</p>
        </div>
        <Link to="/panel/compras" className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1" style={{ color }}>
          <ExternalLink className="w-3 h-3" /> Ir a Compras (global)
        </Link>
      </div>
      {loading ? <p className="text-sm text-stone-500">Cargando...</p>
        : items.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="p-8 text-center text-stone-500">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin compras asociadas a esta unidad.</p>
              <p className="text-xs text-stone-400 mt-1">Las órdenes se generan desde Menús → &quot;Generar orden de compra&quot;.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="border border-stone-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500 text-left">
                <tr>
                  <th className="p-2">Fecha</th><th className="p-2">Producto / Menú</th>
                  <th className="p-2 text-right">Cant.</th><th className="p-2 text-right">Total</th>
                  <th className="p-2">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.compra_id} className="border-t border-stone-100" data-testid={`compra-${c.compra_id}`}>
                    <td className="p-2 text-xs text-stone-500">{c.fecha || c.created_at?.slice(0, 10)}</td>
                    <td className="p-2">{c.menu_nombre || c.producto || "—"}</td>
                    <td className="p-2 text-right">{c.cantidad || c.lineas?.length || "—"}</td>
                    <td className="p-2 text-right font-medium">{fmt(c.total || c.costo_total)}</td>
                    <td className="p-2 text-xs">{c.estatus || "pendiente"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PRODUCCIÓN TAB · producciones filtradas por unidad
   ═══════════════════════════════════════════════════════════════════ */
export function ProduccionTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/produccion?unidad_negocio=${unidadSlug}`);
      setItems(r.data || []);
    } catch { toast.error("Error al cargar producciones"); }
    finally { setLoading(false); }
  }, [unidadSlug]);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4" data-testid={`produccion-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Producción</h2>
          <p className="text-stone-500 text-sm">Órdenes de producción vinculadas a eventos de esta unidad.</p>
        </div>
        <Link to="/panel/produccion" className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1" style={{ color }}>
          <ExternalLink className="w-3 h-3" /> Ir a Producción (global)
        </Link>
      </div>
      {loading ? <p className="text-sm text-stone-500">Cargando...</p>
        : items.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="p-8 text-center text-stone-500">
              <Factory className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin producciones asociadas.</p>
              <p className="text-xs text-stone-400 mt-1">Las producciones se generan desde el Expediente del evento.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => (
              <Card key={p.produccion_id} className="border-stone-200" data-testid={`prod-${p.produccion_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{p.evento_nombre || "(sin nombre)"}</p>
                      <p className="text-xs text-stone-500">{p.cliente_nombre}</p>
                      <p className="text-xs text-stone-400 mt-1">{p.fecha_evento}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-stone-100 text-stone-700 capitalize">{(p.estatus || "").replace("_", " ")}</span>
                  </div>
                  <p className="text-xs mt-2 text-stone-500">{p.invitados || 0} invitados</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PROVEEDORES TAB
   ═══════════════════════════════════════════════════════════════════ */
export function ProveedoresTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/proveedores?unidad_negocio=${unidadSlug}`);
      setItems(r.data || []);
    } catch { toast.error("Error al cargar proveedores"); }
    finally { setLoading(false); }
  }, [unidadSlug]);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4" data-testid={`proveedores-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Proveedores</h2>
          <p className="text-stone-500 text-sm">Proveedores registrados para esta unidad.</p>
        </div>
        <Link to="/panel/proveedores" className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1" style={{ color }}>
          <ExternalLink className="w-3 h-3" /> Ir a Proveedores (global)
        </Link>
      </div>
      {loading ? <p className="text-sm text-stone-500">Cargando...</p>
        : items.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="p-8 text-center text-stone-500">
              <Truck className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin proveedores asignados a esta unidad.</p>
              <Link to="/panel/admin/sin-clasificar" className="text-xs underline text-amber-700 mt-2 inline-block">
                Ver proveedores sin clasificar
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => (
              <Card key={p.proveedor_id} className="border-stone-200" data-testid={`prov-${p.proveedor_id}`}>
                <CardContent className="p-4">
                  <p className="font-medium">{p.nombre}</p>
                  <p className="text-xs text-stone-500 capitalize">{p.categoria || "—"}</p>
                  {p.telefono && <p className="text-xs text-stone-600 mt-1">Tel: {p.telefono}</p>}
                  {p.correo && <p className="text-xs text-stone-500 truncate">{p.correo}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CATÁLOGO TAB · items del catálogo filtrados por unidad
   ═══════════════════════════════════════════════════════════════════ */
export function CatalogoTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/catalogo?unidad_negocio=${unidadSlug}`);
      setItems(r.data || []);
    } catch { toast.error("Error al cargar catálogo"); }
    finally { setLoading(false); }
  }, [unidadSlug]);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4" data-testid={`catalogo-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Catálogo</h2>
          <p className="text-stone-500 text-sm">Servicios y productos del catálogo asignados a esta unidad.</p>
        </div>
        <Link to="/panel/catalogo" className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1" style={{ color }}>
          <ExternalLink className="w-3 h-3" /> Ir a Catálogo (global)
        </Link>
      </div>
      {loading ? <p className="text-sm text-stone-500">Cargando...</p>
        : items.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="p-8 text-center text-stone-500">
              <Layers className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin items en catálogo para esta unidad.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => (
              <Card key={it.item_id} className="border-stone-200 overflow-hidden" data-testid={`cat-${it.item_id}`}>
                {it.imagen_url && (
                  <div className="h-32 bg-stone-100 overflow-hidden">
                    <img src={it.imagen_url} alt={it.nombre} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-3">
                  <p className="font-medium text-sm truncate">{it.nombre}</p>
                  <p className="text-[10px] text-stone-500 capitalize">{it.categoria}</p>
                  {it.precio > 0 && <p className="font-semibold mt-1" style={{ color }}>{fmt(it.precio)}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   COSTEO TAB · dashboard read-only agregado
   ═══════════════════════════════════════════════════════════════════ */
export function CosteoTab({ unidadSlug, color, colorSoft }) {
  const [data, setData] = useState({ compras_total: 0, produccion_count: 0, recetas_costo_promedio: 0, valor_inventario: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = `?unidad_negocio=${unidadSlug}`;
        const [compras, produccion, resumen] = await Promise.all([
          axios.get(`${API}/compras${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/produccion${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/inventario/resumen${q}`).catch(() => ({ data: null })),
        ]);
        const ct = (compras.data || []).reduce((s, c) => s + (c.total || c.costo_total || 0), 0);
        setData({
          compras_total: ct,
          compras_count: compras.data?.length || 0,
          produccion_count: produccion.data?.length || 0,
          valor_inventario: resumen.data?.valor_total || 0,
        });
      } finally { setLoading(false); }
    })();
  }, [unidadSlug]);

  if (loading) return <p className="text-sm text-stone-500">Cargando costeo...</p>;

  return (
    <div className="space-y-4" data-testid={`costeo-tab-${unidadSlug}`}>
      <div>
        <h2 className="font-display text-2xl text-stone-900">Costeo</h2>
        <p className="text-stone-500 text-sm">Vista agregada · read-only. La edición se hace en cada módulo.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-stone-200"><CardContent className="p-4"><p className="text-[10px] text-stone-500 uppercase">Órdenes de compra</p><p className="font-display text-2xl">{data.compras_count}</p></CardContent></Card>
        <Card className="border-stone-200"><CardContent className="p-4"><p className="text-[10px] text-stone-500 uppercase">Total compras</p><p className="font-display text-2xl">{fmt(data.compras_total)}</p></CardContent></Card>
        <Card className="border-stone-200"><CardContent className="p-4"><p className="text-[10px] text-stone-500 uppercase">Producciones</p><p className="font-display text-2xl">{data.produccion_count}</p></CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-4"><p className="text-[10px] text-stone-500 uppercase">Valor inventario</p><p className="font-display text-2xl text-emerald-800">{fmt(data.valor_inventario)}</p></CardContent></Card>
      </div>
      <div className="text-xs text-stone-500 p-3 rounded-lg" style={{ background: colorSoft }}>
        <p className="font-medium mb-1" style={{ color }}>Sobre esta vista</p>
        <p>Dashboard agregado que suma compras totales, producciones y valor de stock actual de esta unidad. Se calcula en vivo desde los endpoints operativos filtrados por <code>unidad_negocio</code>. Para editar, ve al módulo correspondiente (Compras / Producción / Inventario).</p>
      </div>
    </div>
  );
}
